import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, ChevronDown, Instagram, Facebook, Youtube, Twitter } from 'lucide-react';
import { useSettingsStore } from '../stores/settingsStore';

const footerLinks = {
  'Popüler Ürünler': [
    { label: 'Split Klimalar', href: '/kategori/split-klimalar' },
    { label: 'Kombi', href: '/kategori/kombi' },
    { label: 'Hava Temizleyici', href: '/kategori/hava-temizleyici' },
    { label: 'Mühendislik Çözümleri', href: '/kategori/ticari-klimalar' },
    { label: 'Ayın Fırsatları', href: '/arama?q=indirimli' },
    { label: 'Daikin Premium', href: '/marka/daikin' },
  ],
  'Kurumsal': [
    { label: 'Hakkımızda', href: '/hakkimizda' },
    { label: 'İletişim', href: '/iletisim' },
    { label: 'Blog', href: '/blog' },
    { label: 'SSS', href: '/sss' },
  ],
  'Sözleşmeler': [
    { label: 'KVKK / Gizlilik Politikası', href: '/gizlilik-politikasi' },
    { label: 'Çerez Politikası', href: '/cerez-politikasi' },
    { label: 'Kullanım Koşulları', href: '/kullanim-kosullari' },
  ],
};

const paymentMethods = [
  { name: 'bonus', label: 'Bonus' },
  { name: 'cardfinans', label: 'CardFinans' },
  { name: 'world', label: 'WORLD' },
  { name: 'bankkart', label: 'Bankkart' },
  { name: 'axess', label: 'Axess' },
  { name: 'maximum', label: 'Maximum' },
  { name: 'troy', label: 'Troy' },
  { name: 'visa', label: 'VISA' },
  { name: 'mastercard', label: 'Mastercard' },
];

const FooterAccordion: React.FC<{ title: string; links: { label: string; href: string }[] }> = ({ title, links }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-surface-100 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="font-semibold text-surface-800 text-sm">{title}</span>
        <ChevronDown className={`w-5 h-5 text-surface-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="pb-4 space-y-2.5">
          {links.map((link) => (
            <Link key={link.href} to={link.href} className="block text-sm text-surface-500 hover:text-[#0891b2] transition-colors pl-1">
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const {
    phoneNumber, phone2, email,
    address, workingHoursWeekday, workingHoursSaturday, workingHoursSunday,
    instagram, facebook, youtube, twitter,
  } = useSettingsStore();

  const socialLinks = [
    { url: instagram, icon: <Instagram size={16} />, label: 'Instagram' },
    { url: facebook, icon: <Facebook size={16} />, label: 'Facebook' },
    { url: youtube, icon: <Youtube size={16} />, label: 'YouTube' },
    { url: twitter, icon: <Twitter size={16} />, label: 'Twitter' },
  ].filter(s => s.url);

  return (
    <>
      <footer className="bg-white border-t border-surface-100">
        <div className="max-w-[1320px] mx-auto px-4 py-8 lg:py-12">

          {/* Desktop: 4-column grid */}
          <div className="hidden lg:grid grid-cols-4 gap-8">
            {/* Company */}
            <div>
              <Link to="/" className="flex items-center gap-1 mb-4">
                <span className="text-xl font-extrabold text-surface-900">AR <span className="text-[#0891b2]">KLİMA</span></span>
              </Link>
              <div className="space-y-2 mb-4">
                <a href={`tel:${(phoneNumber || '').replace(/\s/g, '')}`} className="flex items-center gap-2 text-surface-600 hover:text-[#0891b2] transition-colors text-sm">
                  <Phone className="w-4 h-4 text-[#0891b2]" />
                  <span>Müşteri Hizmetleri:</span>
                  <strong>{phoneNumber}</strong>
                </a>
                {phone2 && (
                  <a href={`tel:${(phone2 || '').replace(/\s/g, '')}`} className="flex items-center gap-2 text-surface-600 hover:text-[#0891b2] transition-colors text-sm">
                    <Phone className="w-4 h-4 text-[#0891b2]" />
                    <strong>{phone2}</strong>
                  </a>
                )}
                <a href={`mailto:${email}`} className="flex items-center gap-2 text-surface-600 hover:text-[#0891b2] transition-colors text-sm">
                  <Mail className="w-4 h-4 text-[#0891b2]" />
                  <span>{email}</span>
                </a>
              </div>
              <p className="text-xs text-surface-500 leading-relaxed mb-3">
                AR Klima İklimlendirme, 2005 yılında kurulmuş. Daikin Yetkili Satıcısı olarak kaliteli iklimlendirme çözümleri sunmaktayız.
              </p>
              {socialLinks.length > 0 && (
                <div className="flex items-center gap-3 mt-3">
                  {socialLinks.map((s) => (
                    <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#0891b2]/10 flex items-center justify-center text-[#0891b2] hover:bg-[#0891b2] hover:text-white transition-colors" title={s.label}>
                      {s.icon}
                    </a>
                  ))}
                  {socialLinks.length === 0 && (
                    <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#0891b2]/10 flex items-center justify-center text-[#0891b2] hover:bg-[#0891b2] hover:text-white transition-colors">
                      <Instagram size={16} />
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Popüler Ürünler */}
            <div>
              <h3 className="font-bold text-surface-900 mb-4 text-sm">Popüler Ürünler</h3>
              <ul className="space-y-2.5">
                {footerLinks['Popüler Ürünler'].map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="text-sm text-surface-500 hover:text-[#0891b2] transition-colors">{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Kurumsal */}
            <div>
              <h3 className="font-bold text-surface-900 mb-4 text-sm">Kurumsal</h3>
              <ul className="space-y-2.5 mb-6">
                {footerLinks['Kurumsal'].map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="text-sm text-surface-500 hover:text-[#0891b2] transition-colors">{link.label}</Link>
                  </li>
                ))}
              </ul>
              <h3 className="font-bold text-surface-900 mb-4 text-sm">Sözleşmeler</h3>
              <ul className="space-y-2.5">
                {footerLinks['Sözleşmeler'].map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="text-sm text-surface-500 hover:text-[#0891b2] transition-colors">{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact / Map */}
            <div>
              <h3 className="font-bold text-surface-900 mb-4 text-sm">Mağazamız</h3>
              <p className="text-sm text-surface-500 leading-relaxed mb-4">
                {address || 'İzmir, Türkiye'}<br />
                {workingHoursWeekday && <>Pazartesi–Cuma: {workingHoursWeekday}<br /></>}
                {workingHoursSaturday && <>Cumartesi: {workingHoursSaturday}<br /></>}
                {workingHoursSunday && <>Pazar: {workingHoursSunday}</>}
              </p>
              <Link to="/iletisim" className="text-sm font-semibold text-[#0891b2] hover:underline">
                Bize Ulaşın →
              </Link>
            </div>
          </div>

          {/* Mobile: accordion */}
          <div className="lg:hidden">
            <div className="mb-5">
              <Link to="/" className="text-xl font-extrabold text-surface-900">AR <span className="text-[#0891b2]">KLİMA</span></Link>
              <div className="mt-3 space-y-1.5">
                <a href={`tel:${(phoneNumber || '').replace(/\s/g, '')}`} className="flex items-center gap-2 text-surface-600 text-sm">
                  <Phone className="w-4 h-4 text-[#0891b2]" />
                  <strong>{phoneNumber}</strong>
                </a>
                {phone2 && (
                  <a href={`tel:${(phone2 || '').replace(/\s/g, '')}`} className="flex items-center gap-2 text-surface-600 text-sm">
                    <Phone className="w-4 h-4 text-[#0891b2]" />
                    <strong>{phone2}</strong>
                  </a>
                )}
                <a href={`mailto:${email}`} className="flex items-center gap-2 text-surface-600 text-sm">
                  <Mail className="w-4 h-4 text-[#0891b2]" />
                  {email}
                </a>
              </div>
            </div>
            {Object.entries(footerLinks).map(([title, links]) => (
              <FooterAccordion key={title} title={title} links={links} />
            ))}
          </div>

        </div>
      </footer>

      {/* Payment methods bar */}
      <div className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-[1320px] mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-surface-400">
              © {currentYear} AR Klima İklimlendirme. Tüm hakları saklıdır.
            </p>
            <div className="flex flex-wrap items-center gap-2 justify-center">
              {paymentMethods.map((method) => (
                <span
                  key={method.name}
                  className="bg-white border border-gray-200 rounded px-2 py-1 text-[10px] font-semibold text-gray-500 shadow-sm"
                >
                  {method.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Footer;
